<template>
  <div class="v2-profile v2-container v2-section">
    <header class="v2-profile__welcome">
      <p class="v2-overline">Mi cuenta</p>
      <h1 class="v2-headline v2-headline--display-lg v2-profile__title">
        Mi Perfil
      </h1>
      <p class="v2-body v2-body--md v2-body--muted">
        Consulta y actualiza tu información personal, fiscal y de directorio.
      </p>
    </header>

    <div v-if="loading" class="v2-profile__loading">
      <v-icon size="32" color="grey lighten-1">mdi-progress-clock</v-icon>
      <p class="v2-body v2-body--sm v2-body--muted">Cargando tu información…</p>
    </div>

    <div v-else class="v2-profile__layout">
      <ProfilePictureUploader :user="user" />

      <nav
        class="v2-profile__tabs"
        role="tablist"
        aria-label="Secciones de Mi Perfil"
      >
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          role="tab"
          :id="`v2-profile-tab-${tab.id}`"
          :aria-selected="activeTab === tab.id"
          :aria-controls="`v2-profile-panel-${tab.id}`"
          :tabindex="activeTab === tab.id ? 0 : -1"
          :class="[
            'v2-profile__tab',
            { 'v2-profile__tab--active': activeTab === tab.id }
          ]"
          @click="setActiveTab(tab.id)"
          @keydown="onTabKey($event, tab.id)"
        >
          <span class="v2-profile__tab-icon" aria-hidden="true">
            <v-icon size="18">{{ tab.icon }}</v-icon>
          </span>
          <span class="v2-profile__tab-label">{{ tab.label }}</span>
        </button>
      </nav>

      <div class="v2-profile__panel" role="tabpanel">
        <!-- Datos del Miembro -->
        <section
          v-show="activeTab === 'personal'"
          :id="'v2-profile-panel-personal'"
          :aria-labelledby="'v2-profile-tab-personal'"
          role="tabpanel"
          class="v2-profile__panel-section"
        >
          <EmptyState
            v-if="!sections.length"
            title="Sin secciones configuradas"
            description="Tu organización aún no ha definido los campos del perfil."
            icon="mdi-account-cog-outline"
          />
          <template v-else>
            <nav
              v-if="sections.length > 1"
              class="v2-profile__subtabs"
              role="tablist"
              aria-label="Secciones del formulario"
            >
              <button
                v-for="section in sections"
                :key="section.id"
                type="button"
                role="tab"
                :id="`v2-profile-subtab-${section.id}`"
                :aria-selected="activeSectionId === section.id"
                :aria-controls="`v2-profile-subpanel-${section.id}`"
                :tabindex="activeSectionId === section.id ? 0 : -1"
                :class="[
                  'v2-profile__subtab',
                  { 'v2-profile__subtab--active': activeSectionId === section.id }
                ]"
                @click="setActiveSection(section.id)"
                @keydown="onSubTabKey($event, section.id)"
              >
                {{ section.name }}
              </button>
            </nav>

            <ProfileSection
              v-for="section in sections"
              v-show="activeSectionId === section.id"
              :key="section.id"
              :id="`v2-profile-subpanel-${section.id}`"
              :section="section"
              :editing="editingSectionId === section.id"
              :saving="saving"
              :errors="errorsBySection[section.id] || {}"
              :read-only="false"
              @edit="onEditSection(section.id)"
              @cancel="onCancelEdit"
              @save="onSaveSection(section.id)"
              @field-update="onFieldUpdate"
            />
          </template>
        </section>

        <!-- Datos Fiscales -->
        <section
          v-show="activeTab === 'fiscal'"
          :id="'v2-profile-panel-fiscal'"
          :aria-labelledby="'v2-profile-tab-fiscal'"
          role="tabpanel"
          class="v2-profile__panel-section"
        >
          <FiscalDataTab :user="user" />
        </section>

        <!-- Datos del Directorio -->
        <section
          v-show="activeTab === 'directory'"
          :id="'v2-profile-panel-directory'"
          :aria-labelledby="'v2-profile-tab-directory'"
          role="tabpanel"
          class="v2-profile__panel-section"
        >
          <DirectoryDataTab :user="user" />
        </section>

        <!-- Cambiar Contraseña -->
        <section
          v-show="activeTab === 'password'"
          :id="'v2-profile-panel-password'"
          :aria-labelledby="'v2-profile-tab-password'"
          role="tabpanel"
          class="v2-profile__panel-section"
        >
          <ChangePasswordTab />
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import { mapActions, mapMutations, mapState } from "vuex";
import EmptyState from "@/components/memberDashboardV2/ui/EmptyState.vue";
import ProfileSection from "./ProfileSection.vue";
import ProfilePictureUploader from "./ProfilePictureUploader.vue";
import FiscalDataTab from "./FiscalDataTab.vue";
import DirectoryDataTab from "./DirectoryDataTab.vue";
import ChangePasswordTab from "./ChangePasswordTab.vue";
import { buildProfileSections, expandFieldInstances } from "./profileHelpers";

const TABS = [
  { id: "personal", label: "Datos del Miembro", icon: "mdi-account-outline" },
  { id: "fiscal", label: "Datos Fiscales", icon: "mdi-receipt-text-outline" },
  { id: "directory", label: "Datos del Directorio", icon: "mdi-book-account-outline" },
  { id: "password", label: "Cambiar Contraseña", icon: "mdi-lock-reset" },
];

export default {
  name: "MemberDashboardV2Profile",
  components: {
    EmptyState,
    ProfileSection,
    ProfilePictureUploader,
    FiscalDataTab,
    DirectoryDataTab,
    ChangePasswordTab,
  },
  data() {
    return {
      tabs: TABS,
      activeTab: "personal",
      activeSectionId: null,
      loading: false,
      saving: false,
      sections: [],
      editingSectionId: null,
      pendingValues: {},
      errorsBySection: {},
    };
  },
  computed: {
    ...mapState("user", ["user"]),
    headOfficeId() {
      if (!this.user) return null;
      if (this.user.headOffice) {
        if (typeof this.user.headOffice === "object") {
          return this.user.headOffice._id || this.user.headOffice.id;
        }
        return this.user.headOffice;
      }
      return this.user._id;
    },
  },
  watch: {
    sections: {
      immediate: true,
      handler(sections) {
        if (!sections.length) {
          this.activeSectionId = null;
          return;
        }
        if (!this.activeSectionId || !sections.some((s) => s.id === this.activeSectionId)) {
          this.activeSectionId = sections[0].id;
        }
      },
    },
  },
  methods: {
    ...mapActions("registrationFields", ["getRegistrationFields"]),
    ...mapActions("user", ["fetchProfileData"]),
    ...mapMutations("notification", ["show"]),
    setActiveTab(tabId) {
      if (this.editingSectionId) {
        this.onCancelEdit();
      }
      this.activeTab = tabId;
    },
    setActiveSection(sectionId) {
      if (this.editingSectionId && this.editingSectionId !== sectionId) {
        this.onCancelEdit();
      }
      this.activeSectionId = sectionId;
    },
    onTabKey(event, tabId) {
      const ids = this.tabs.map((t) => t.id);
      const currentIndex = ids.indexOf(tabId);
      let nextIndex = currentIndex;
      if (event.key === "ArrowRight") {
        nextIndex = (currentIndex + 1) % ids.length;
        event.preventDefault();
      } else if (event.key === "ArrowLeft") {
        nextIndex = (currentIndex - 1 + ids.length) % ids.length;
        event.preventDefault();
      } else if (event.key === "Home") {
        nextIndex = 0;
        event.preventDefault();
      } else if (event.key === "End") {
        nextIndex = ids.length - 1;
        event.preventDefault();
      } else {
        return;
      }
      this.setActiveTab(ids[nextIndex]);
      const nextTab = document.getElementById(`v2-profile-tab-${ids[nextIndex]}`);
      if (nextTab) nextTab.focus();
    },
    onSubTabKey(event, sectionId) {
      const ids = this.sections.map((s) => s.id);
      const currentIndex = ids.indexOf(sectionId);
      let nextIndex = currentIndex;
      if (event.key === "ArrowRight") {
        nextIndex = (currentIndex + 1) % ids.length;
        event.preventDefault();
      } else if (event.key === "ArrowLeft") {
        nextIndex = (currentIndex - 1 + ids.length) % ids.length;
        event.preventDefault();
      } else if (event.key === "Home") {
        nextIndex = 0;
        event.preventDefault();
      } else if (event.key === "End") {
        nextIndex = ids.length - 1;
        event.preventDefault();
      } else {
        return;
      }
      this.setActiveSection(ids[nextIndex]);
      const nextTab = document.getElementById(`v2-profile-subtab-${ids[nextIndex]}`);
      if (nextTab) nextTab.focus();
    },
    async loadProfile() {
      if (!this.headOfficeId) return;
      this.loading = true;
      try {
        const [fieldsResult, sectionsResult] = await Promise.all([
          this.getRegistrationFields(this.headOfficeId),
          Vue.axios({ method: "GET", url: `/formSections/form/${this.headOfficeId}` })
            .then((r) => ({ status: true, data: r.data }))
            .catch(() => ({ status: false, data: { sections: [] } })),
        ]);
        const registrationFields = fieldsResult && fieldsResult.data
          ? (fieldsResult.data.data || fieldsResult.data || [])
          : [];
        const formSections = sectionsResult && sectionsResult.data
          ? (sectionsResult.data.sections || [])
          : [];
        this.sections = buildProfileSections({
          user: this.user,
          registrationFields,
          formSections,
        });
      } catch (e) {
        this.sections = [];
      } finally {
        this.loading = false;
      }
    },
    onEditSection(sectionId) {
      this.editingSectionId = sectionId;
      this.activeSectionId = sectionId;
      this.pendingValues = {};
      this.errorsBySection = { ...this.errorsBySection, [sectionId]: {} };
    },
    onCancelEdit() {
      this.editingSectionId = null;
      this.pendingValues = {};
    },
    onFieldUpdate({ key, value, isStatic, sectionId }) {
      this.sections = this.sections.map((section) => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          fields: section.fields.map((field) => {
            if (isStatic) {
              return field.key === key ? { ...field, value } : field;
            }
            return field.formattedName === key ? { ...field, value } : field;
          }),
        };
      });
      this.pendingValues = { ...this.pendingValues, [key]: value };
    },
    isEmptyRequiredValue(value) {
      if (value === null || value === undefined) return true;
      if (typeof value === "string") return value.trim() === "";
      if (Array.isArray(value)) return value.length === 0;
      if (typeof value === "object") {
        return Object.keys(value).length === 0 || Object.values(value).every((item) => this.isEmptyRequiredValue(item));
      }
      return false;
    },
    validateRequiredFields() {
      const errorsBySection = {};
      let firstMissing = null;

      this.sections.forEach((section) => {
        const sectionErrors = {};
        section.fields.forEach((field) => {
          const instances = expandFieldInstances(field, section.fields);
          instances.forEach((instance) => {
            const renderKey = instances.length > 1
              ? `${field.key || field.formattedName}-${instance._instanceKey}`
              : field.key || field.formattedName;
            instance._renderKey = renderKey;
            const key = field.key || field.formattedName;
            if (!instance.required || !key || !this.isEmptyRequiredValue(instance.value)) return;
            sectionErrors[key] = "Este campo es obligatorio";
            if (!firstMissing) {
              firstMissing = { sectionId: section.id, fieldId: renderKey };
            }
          });
        });
        if (Object.keys(sectionErrors).length) errorsBySection[section.id] = sectionErrors;
      });

      return { ok: !firstMissing, errorsBySection, firstMissing };
    },
    scrollToMissingField(fieldId) {
      if (!fieldId) return;
      this.$nextTick(() => {
        const anchor = document.getElementById(
          `v2-profile-field-${String(fieldId).replace(/[^a-zA-Z0-9_-]/g, "-")}`
        );
        if (!anchor) return;
        anchor.scrollIntoView({ behavior: "smooth", block: "center" });
        const input = anchor.querySelector("input, textarea, select");
        if (input) input.focus({ preventScroll: true });
      });
    },
    async onSaveSection(sectionId) {
      this.saving = true;
      this.errorsBySection = { ...this.errorsBySection, [sectionId]: {} };
      try {
        const section = this.sections.find((s) => s.id === sectionId);
        if (!section) throw new Error("Sección no encontrada");

        const requiredValidation = this.validateRequiredFields();
        if (!requiredValidation.ok) {
          this.errorsBySection = requiredValidation.errorsBySection;
          this.activeSectionId = requiredValidation.firstMissing.sectionId;
          this.saving = false;
          this.scrollToMissingField(requiredValidation.firstMissing.fieldId);
          return;
        }

        const pendingByKey = this.pendingValues;
        const hasPending = (key) => Object.prototype.hasOwnProperty.call(pendingByKey, key);

        const staticUpdates = {};
        section.fields.forEach((field) => {
          if (!field.isStatic) return;
          const key = field.key;
          if (!key || !hasPending(key)) return;
          staticUpdates[key] = pendingByKey[key];
        });

        const baseMemberData = Array.isArray(this.user && this.user.memberData)
          ? this.user.memberData.map((entry) => ({ ...entry }))
          : [];
        const baseByFormattedName = new Map(
          baseMemberData
            .filter((entry) => entry && entry.formattedName)
            .map((entry) => [String(entry.formattedName), entry])
        );

        const allVisibleFields = this.sections.flatMap((s) => s.fields);
        allVisibleFields.forEach((field) => {
          if (field.isStatic) return;
          if (!field.formattedName) return;
          const fieldKey = field.formattedName;
          const nextValue = hasPending(fieldKey)
            ? pendingByKey[fieldKey]
            : field.value;
          const existing = baseByFormattedName.get(String(fieldKey));
          if (existing) {
            existing.value = nextValue;
            return;
          }
          baseMemberData.push({
            _id: field._id,
            name: field.name,
            formattedName: fieldKey,
            fieldType: field.fieldType,
            value: nextValue,
          });
        });

        const userId = this.user && this.user._id;
        const requestData = {
          ...staticUpdates,
          memberData: baseMemberData,
        };
        await Vue.axios({
          method: "PUT",
          url: `/members/form/${userId}`,
          data: requestData,
        });
        this.editingSectionId = null;
        this.pendingValues = {};
        await this.fetchProfileData();
        await this.loadProfile();
        try {
          this.show({
            text: "Usuario actualizado exitosamente",
            color: "success",
          });
        } catch (_) {
          /* el toast nunca debe romper el guardado */
        }
      } catch (e) {
        const message =
          (e && e.response && e.response.data &&
            (e.response.data.error?.message || e.response.data.message)) ||
          e.message ||
          "No se pudo guardar";
        this.errorsBySection = {
          ...this.errorsBySection,
          [sectionId]: { _form: message },
        };
        try {
          this.show({ text: message, color: "error" });
        } catch (_) {
          /* el toast nunca debe romper el manejo del error */
        }
      } finally {
        this.saving = false;
      }
    },
  },
  async mounted() {
    await this.loadProfile();
  },
};
</script>

<style scoped>
.v2-profile {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.v2-profile__welcome {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.v2-profile__title {
  margin: 0;
}

.v2-profile__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-8);
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
}

.v2-profile__layout {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.v2-profile__tabs {
  display: flex;
  flex-direction: row;
  gap: var(--space-1);
  overflow-x: auto;
  padding: 0;
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xs);
  scrollbar-width: thin;
}

.v2-profile__tab {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-5);
  background: transparent;
  border: 0;
  border-bottom: 3px solid transparent;
  border-radius: 0;
  color: var(--color-text-muted);
  font-family: var(--font-body);
  font-size: var(--fs-body-sm);
  font-weight: var(--fw-medium);
  text-align: left;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: color var(--transition-fast), background-color var(--transition-fast), border-color var(--transition-fast);
}

.v2-profile__tab:hover {
  background-color: var(--color-neutral-100);
  color: var(--color-text);
}

.v2-profile__tab:focus-visible {
  outline: 2px solid var(--color-amber);
  outline-offset: -2px;
}

.v2-profile__tab--active {
  background-color: transparent;
  color: var(--color-amber-on);
  font-weight: var(--fw-semibold);
  border-bottom-color: var(--color-amber);
}

.v2-profile__tab--active:hover {
  background-color: var(--color-neutral-100);
  color: var(--color-amber-on);
}

.v2-profile__tab-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.v2-profile__tab-label {
  line-height: var(--lh-snug);
}

.v2-profile__panel {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
  min-width: 0;
}

.v2-profile__panel-section {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

/* =========================================================
   SUBNAVEGACIÓN — secciones del formulario dentro de Datos del Miembro
   ========================================================= */
.v2-profile__subtabs {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: var(--space-1);
  padding: var(--space-1);
  background-color: var(--color-neutral-50);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
}

.v2-profile__subtab {
  display: inline-flex;
  align-items: center;
  padding: var(--space-2) var(--space-4);
  background: transparent;
  border: 0;
  border-radius: var(--radius-md);
  color: var(--color-text-muted);
  font-family: var(--font-body);
  font-size: var(--fs-body-sm);
  font-weight: var(--fw-medium);
  text-align: left;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: color var(--transition-fast), background-color var(--transition-fast);
}

.v2-profile__subtab:hover {
  background-color: var(--color-surface);
  color: var(--color-text);
}

.v2-profile__subtab:focus-visible {
  outline: 2px solid var(--color-amber);
  outline-offset: 2px;
}

.v2-profile__subtab--active {
  background-color: var(--color-surface);
  color: var(--color-amber-on);
  font-weight: var(--fw-semibold);
  box-shadow: var(--shadow-xs);
}

.v2-profile__subtab--active:hover {
  background-color: var(--color-surface);
  color: var(--color-amber-on);
}
</style>
